/**
 * usePWAInstall
 * Hook to handle PWA installation functionality
 */

import { useCallback, useEffect, useState } from 'react'
import { logger } from '@shared/lib/utils/logger'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export interface PWAInstallState {
  isInstallable: boolean
  isInstalled: boolean
  isInstalling: boolean
  promptInstall: () => Promise<boolean>
}

interface ExtenderNavigator extends Navigator {
  standalone?: boolean
}

export function usePWAInstall(): PWAInstallState {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isInstalling, setIsInstalling] = useState(false)

  useEffect(() => {
    // Check if app is already installed (standalone mode)
    const checkInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      const isIOSStandalone = (window.navigator as ExtenderNavigator).standalone === true
      return isStandalone || isIOSStandalone
    }

    setIsInstalled(checkInstalled())

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const promptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)
      setIsInstallable(true)
    }

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsInstallable(false)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  /**
   * Trigger the install prompt
   * Returns true if user accepted, false if dismissed
   */
  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      logger.warn('Install prompt not available')
      return false
    }

    try {
      setIsInstalling(true)

      // Show the install prompt
      await deferredPrompt.prompt()

      // Wait for the user's response
      const choiceResult = await deferredPrompt.userChoice

      // Clear the deferred prompt
      setDeferredPrompt(null)
      setIsInstallable(false)

      if (choiceResult.outcome === 'accepted') {
        return true
      } else {
        return false
      }
    } catch (error) {
      logger.error('Error showing install prompt:', error)
      return false
    } finally {
      setIsInstalling(false)
    }
  }, [deferredPrompt])

  return {
    isInstallable,
    isInstalled,
    isInstalling,
    promptInstall,
  }
}
